import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import FormQuestions from './createQuestion.component'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import styled from 'styled-components'
import StringField from '~/prix/react/components/form/field/stringField'
import DateField from '~/prix/react/components/form/field/dateTimeField'
import { Button } from '~/prix/react/components/button'
import { toastError, toastSuccess } from '~/components/toasts'
import { format } from 'date-fns'
import { NewFieldProps, NewQuestion, QuestionnaireDataType } from './questionnaireTypes'
import EnumeratedField from '~/prix/react/components/form/field/enumeratedField'
import { enumerated } from '~/prix'
import Card from '~/components/card'
import { useNavigate, useParams } from 'react-router'
import fetchQuestionnaireDefinition from './fetch/questionnaireFetch.definition'
import updateQuestionnaireDefinition from './update/questionnaireUpdate.definition'
import { getCitizenPublicTargets } from './questionnairePublicTargets.utils'
import { colors } from '~/design'
import Skeleton from '~/components/skeleton'
import { questionnaireObject } from './questionnaireObject'
import { z } from 'zod'

type UpdateQuestionnaireInput = z.infer<typeof questionnaireObject>

function readTargetPublicValue(targetPublic: string | null | undefined) {
  if (!targetPublic) {
    return null
  }

  try {
    const parsedTargetPublic = JSON.parse(targetPublic) as { value?: string }
    return parsedTargetPublic.value ?? null
  } catch {
    return targetPublic
  }
}

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.padding(2)};
  margin-bottom: ${({ theme }) => theme.padding(2)};

  .questionary {
    display: flex;
    width: 100%;
    padding: 0 ${({ theme }) => theme.padding(3)} ${({ theme }) => theme.padding(4)} ${({ theme }) => theme.padding(3)};
    align-items: center;

    .questionary-column {
      display: flex;
      width: 100%;
      align-items: center;
      flex-direction: column;

      h2 {
        text-align: center;
        margin-bottom: ${({ theme }) => theme.padding(2)};
      }

      form {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.padding(2)};
        padding: 10px 15px;
      }

      .custom-select__control {
        height: 45px;
        border-radius: 8px;
        padding: 0.8rem 1rem;
        margin: 4px 0px 2px 0px;
      }

      .react-datepicker-wrapper {
        width: 100%;
      }

      input {
        flex: 1;
        width: 100%;
      }
    }
  }

  .button-questionary {
    padding: ${({ theme }) => theme.padding(2)} 0 ${({ theme }) => theme.padding(3)} 0;
    display: flex;
    justify-content: center;
    width: 100%;
    
    button {
      height: 45px;
      min-width: 160px;
      background-color: ${colors.prixOrange};
      font-weight: 700;
      border-radius: 8px;
    }
  }
`

export default function UpdateQuestionnaire() {
  const [formFields, setFormFields] = useState<NewQuestion[]>([])
  const [loading, setLoading] = useState(true)

  const navigate = useNavigate()
  const params = useParams()
  const { id } = params

  const { publicTargets, publicTargetsEnum } = useMemo(() => {
    const publicTargets = getCitizenPublicTargets()
    const publicTargetsEnum = enumerated(
      publicTargets.reduce((acc, target) => {
        acc[target.value] = target.label
        return acc
      }, {} as Record<string, string>),
    )

    return { publicTargets, publicTargetsEnum }
  }, [])

  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireDataType | null>(null)

  const [newField, setNewField] = useState<NewFieldProps | null>({
    title: '',
    observation: null,
    type: '',
    required: true,
    ordinalPosition: 1,
    deletedAt: null,
    questionOptions: [],
    childQuestions: null,
  })

  const { callAction: callActionFetchQuestionnaire } = useActionFunction(
    fetchQuestionnaireDefinition,
  )

  useLayoutEffect(() => {
    const fetchQuestionnaire = async () => {
      if (!id) {
        setLoading(false)
        return
      }

      const result = await callActionFetchQuestionnaire({
        id: Number(id),
        onlyActive: false,
      }).resultPromise
      setQuestionnaireData({
        id: result.id,
        title: result.title,
        observation: result.observation ?? '',
        targetPublic: readTargetPublicValue(result.targetPublic),
        targetType: result.targetType || 'citizen',
        delimitationType: null,
        delimitationId: null,
        delimitationCustom: null,
        startDate: result.startDate ? format(new Date(result.startDate), 'yyyy-MM-dd') : '',
        endDate: result.endDate ? format(new Date(result.endDate), 'yyyy-MM-dd') : null,
        lgpdDisclaimer: result.lgpdDisclaimer ?? '',
        deletedAt: null,
      })
      setFormFields(result.questions as NewQuestion[])
    }

    fetchQuestionnaire().then(() => {
      setLoading(false)
    })
  }, [id])

  const handleFormFieldsChange = (newInput: any) => {
    setFormFields([...formFields, newInput])
  }

  const { callAction: callActionUpdateQuestionnaire } = useActionFunction(
    updateQuestionnaireDefinition,
  )

  const updateQuestionnaire = useCallback(
    (questionnaire: UpdateQuestionnaireInput) => {
      const { resultPromise } = callActionUpdateQuestionnaire(questionnaire)

      resultPromise
        .then(result => {
          if (result.success) {
            toastSuccess('Questionário alterado com sucesso!')
            setFormFields([])
            navigate('/app/questionnaire/list', { replace: true })
          }
        })
        .catch(error => {
          console.log('Erro ao atualizar questionário:', error.message)
          toastError(error.message || 'Erro ao atualizar questionário')
        })

      return resultPromise
    },
    [],
  )

  const handleSubmit = useCallback(
    (event: React.MouseEvent, data: any) => {
      event.preventDefault()
      try {
        const newFormFields = [...formFields]
        if (newField?.title && newField?.questionOptions.length >= 2) {
          newFormFields.push(newField as NewQuestion)
          setFormFields(newFormFields)
          setNewField({
            title: '',
            observation: null,
            type: '',
            required: true,
            ordinalPosition: 1,
            deletedAt: null,
            questionOptions: [],
            childQuestions: null,
          })
        }
        const questionnaire = {
          ...data,
          targetPublic: questionnaireData?.targetPublic
            ? JSON.stringify(
                publicTargets.find(target => target.value === questionnaireData?.targetPublic),
              )
            : null,
          questions: newFormFields,
        }

        updateQuestionnaire(questionnaire)
      } catch (error) {
        toastError('Erro ao alterar o questionário')
        console.error(error)
      }
    },

    [questionnaireData, formFields, newField, publicTargets, id],
  )

  const handleInputChange = (type: string, value: string | number | null) => {
    setQuestionnaireData(prevData => {
      if (!prevData) return prevData
      return {
        ...prevData,
        [type]: value,
      }
    })
  }

  return (
    <Card title='Alterando o questionário'>
      <Wrapper>
        <div className='questionary'>
          <div className='questionary-column'>
            <form>
              {loading ? (
                <Skeleton width='auto' height={50} />
              ) : (
                <StringField
                  fieldSchema={{
                    currentMeta: {
                      label: 'Título',
                    },
                    currentRules: {
                      isRequired: true,
                    },
                  }}
                  value={questionnaireData?.title}
                  onDirectChange={value => handleInputChange('title', value)}
                  isLoading={loading}
                />
              )}

              {loading ? (
                <Skeleton width='auto' height={50} />
              ) : (
                <StringField
                  fieldSchema={{
                    currentMeta: {
                      label: 'Observações',
                    },
                    currentRules: {
                      isRequired: false,
                    },
                  }}
                  value={questionnaireData?.observation}
                  onDirectChange={value => handleInputChange('observation', value)}
                />
              )}

              {loading ? (
                <Skeleton width='auto' height={50} />
              ) : (
                <EnumeratedField
                  fieldSchema={publicTargetsEnum}
                  value={questionnaireData?.targetPublic}
                  meta={{
                    placeholder: 'Todos',
                    label: 'Público alvo',
                  }}
                  onDirectChange={value => handleInputChange('targetPublic', value)}
                />
              )}

              {loading ? (
                <Skeleton width='auto' height={50} />
              ) : (
                <DateField
                  label='Data inicial'
                  isRequired={true}
                  value={questionnaireData?.startDate || null}
                  onDirectChange={(value: Date) =>
                    handleInputChange('startDate', value ? format(value, 'yyyy-MM-dd') : null)
                  }
                  minDate={new Date()}
                />
              )}

              {loading ? (
                <Skeleton width='auto' height={50} />
              ) : (
                <DateField
                  label='Data final'
                  value={questionnaireData?.endDate || null}
                  onDirectChange={(value: Date | null) =>
                    handleInputChange('endDate', value ? format(value, 'yyyy-MM-dd') : null)
                  }
                  minDate={new Date()}
                />
              )}

              {loading ? (
                <Skeleton width='auto' height={100} />
              ) : (
                <StringField
                  fieldSchema={{
                    currentMeta: {
                      label: 'Isenção de responsabilidade (LGPD)',
                      lines: 4,
                    },
                    currentRules: {
                      isRequired: true,
                    },
                  }}
                  value={questionnaireData?.lgpdDisclaimer}
                  onDirectChange={value => handleInputChange('lgpdDisclaimer', value)}
                />
              )}
            </form>

            <FormQuestions
              formFields={formFields}
              onChange={handleFormFieldsChange}
              setFormFields={setFormFields}
              newField={newField}
              setNewField={setNewField}
              loading={loading}
            />
          </div>
        </div>
        <div className='button-questionary'>
          <Button onClick={e => handleSubmit(e, questionnaireData)}>Alterar questionário</Button>
        </div>
      </Wrapper>
    </Card>
  )
}
